import { Location as ReactLocation } from "react-router-dom";

import { RelatedActivityType } from "./activity";
import { ItsmeUserInfo } from "./auth";
import { InvoiceCategory } from "./invoices";
import { TodoCategory } from "./todo";

export const AppRoute = (function () {
    // Private
    const AddBroker = "/addbroker";
    const FeatureDocuments = "/:filter/documents";
    const FeatureDocumentsSearch = `${FeatureDocuments}/search`;
    const RelatedDocuments = "/:filter/:id/documents";
    const RelatedDocumentsSearch = `${RelatedDocuments}/search`;
    const InvoicePreview = "/invoice";
    const Invoices = "/invoices";
    const InvoiceDetails = `${Invoices}/:id`;
    const InvoiceSearch = `${Invoices}/search`;
    const Claims = "/claims";
    const ClaimDetails = `${Claims}/:id`;
    const ClaimsSearch = `${Claims}/search`;
    const Contracts = "/contracts";
    const ContractDetails = `${Contracts}/:id`;
    const ContractsSearch = `${Contracts}/search`;
    const InsuredGoods = "/insuredgoods";
    const InsuredGoodDetails = `${InsuredGoods}/:id`;
    const InsuredGoodsSearch = `${InsuredGoods}/search`;
    const Conversations = "/conversations";
    const ConversationDetail = `${Conversations}/:id`;
    const ConversationContextual = `${Conversations}/:objectType/:objectId`;
    const ReportDamage = "/reportdamage";
    const ReportSchoolDamage = "/reportschooldamage";
    const CustomerCard = "/customercard";
    const CustomerCardDetail = `${CustomerCard}/:id`;
    const Liability = "/liability";
    const LiabilityDetail = `${Liability}/:id`;
    const CommunicationSalary = "/communicationsalary";
    const CommunicationSalaryDetail = `${CommunicationSalary}/:id`;
    const DocumentsToSign = "/documentstosign";
    const PackagesToSign = "/packagestosign";
    const PackagesToSignWithPolicyholderId = `${PackagesToSign}/:id`;
    const DataVerification = "/data-verification";
    const DataVerificationCompany = `${DataVerification}/company`;
    const DataVerificationCompanyDetail = `${DataVerificationCompany}/:id`;
    const Tariffication = "/tariffication";
    const TarifficationDetail = `${Tariffication}/:id`;
    const Settings = "/settings";
    const ErrorPage = "/error";
    const Policyholders = "/policyholders";
    const PolicyholderDetail = `${Policyholders}/:id`;
    const Relations = "/relation";
    const RelationDetail = `${Relations}/:id`;
    const Documents = "/mydocuments";
    const DocumentsSearch = `${Documents}/search`;
    const Requests = "/requests";
    const RequestsSearch = `${Requests}/search`;
    const AddFamilyMember = "/addfamilymember";
    const ChangeAddress = "/changeAddress";

    // Public
    const Register = "/register";
    const RegisterWithToken = `${Register}/:token`;
    const Invite = "/invite";
    const InviteWithToken = `${Invite}/:token`;
    const ResetPassword = "/resetpassword";
    const ResetPasswordWithToken = `${ResetPassword}/:token`;
    const ItsmeRegister = "/itsmeregister/:token";
    const ItsmeActivation = "/itsmeactivation/:token";
    const Preview = "/preview";
    const Itsme = "/itsme";

    // Shared
    const Root = "/";
    const TermsOfUse = `/terms&conditions`;
    const PrivacyPolicy = `/privacypolicy`;
    const CookiePolicy = `/cookiepolicy`;
    const RelatedActivities = `/:featureType/:id/activities`;

    return Object.freeze({
        Root,
        AddBroker,
        FeatureDocuments,
        FeatureDocumentsSearch,
        RelatedDocuments,
        RelatedDocumentsSearch,
        InvoicePreview,
        Invoices,
        InvoiceDetails,
        InvoiceSearch,
        Claims,
        ClaimDetails,
        ClaimsSearch,
        Contracts,
        ContractDetails,
        ContractsSearch,
        InsuredGoods,
        InsuredGoodDetails,
        InsuredGoodsSearch,
        Conversations,
        ConversationDetail,
        ConversationContextual,
        ReportDamage,
        ReportSchoolDamage,
        CustomerCard,
        CustomerCardDetail,
        Liability,
        LiabilityDetail,
        CommunicationSalary,
        CommunicationSalaryDetail,
        DocumentsToSign,
        PackagesToSign,
        PackagesToSignWithPolicyholderId,
        DataVerification,
        DataVerificationCompany,
        DataVerificationCompanyDetail,
        Tariffication,
        TarifficationDetail,
        Settings,
        Error: ErrorPage,
        Policyholders,
        PolicyholderDetail,
        Relations,
        RelationDetail,
        Documents,
        DocumentsSearch,
        Requests,
        RequestsSearch,
        AddFamilyMember,
        ChangeAddress,
        Register,
        RegisterWithToken,
        Invite,
        InviteWithToken,
        ResetPassword,
        ResetPasswordWithToken,
        ItsmeRegister,
        ItsmeActivation,
        Preview,
        Itsme,
        TermsOfUse,
        PrivacyPolicy,
        CookiePolicy,
        RelatedActivities,
    });
})();

export enum SearchParam {
    OfficeId = "o",
    Language = "l",
}

export enum RefererParam {
    PathName = "pathname",
    SearchParams = "search",
}

export interface Location<State> extends Omit<ReactLocation, "state"> {
    state: State;
}

export type DefaultState = {
    officeId?: string;
    title?: string;
};

export type DocumentsPageParams = {
    filter: MyBrokerApi.DocumentFilter;
};
export type DocumentsRelatedPageParams = {
    id: string;
    filter: MyBrokerApi.DocumentFilter;
};

export type ActivitiesPageState = {
    featureType: RelatedActivityType;
    policyholderId: number;
    ibpAclId: number;
    title: string;
};

export type InviteFromBrokerState = {
    email: string;
    officeId: string;
    date: Date;
};

export type LoginState = {
    unAuthorized?: boolean;
    search?: string;
    pathname?: string;
};

export interface RegisterDefaultState {
    email: string;
    officeId: string;
    step: string;
}

export interface RegisterUnknownState {
    email: string;
    step: string;
}

export interface RegisterUnknownItsme {
    itsmeInfo: ItsmeUserInfo;
    step: string;
}

export type RegisterState = RegisterDefaultState | RegisterUnknownState | RegisterUnknownItsme;

export interface ResetPasswordState {
    email: string;
    officeId: string;
}

export interface DetailPageState {
    policyholderId?: number;
}

export interface ContractDetailPageState extends DetailPageState {
    categoryCode: string;
    stateCode: string;
}

export interface InsuredGoodDetailPageState extends DetailPageState {
    insuredGoodType: string;
    canRequestGreenCard?: boolean;
}

export interface InvoicePageState {
    filterId?: InvoiceCategory;
}

export interface FeaturePageState {
    filterId?: string;
}

export interface DocumentsRelatedPageState {
    title?: string;
}

export interface ReportClaimPageState {
    preSelectedInsuredGoodId?: string;
    preSelectedPolicyholderId?: number;
}

export interface PolicyHolderPageState {
    policyholders?: number[];
    type: TodoCategory;
}

export interface TarifficationPageState {
    defaultVehicleId: string;
}

export interface AddFamilyMemberPageState {
    policyholderId: number;
    policyholderName: string;
}

export interface ChangeAddressPageState {
    policyholderId?: number;
}

export interface ContextualConversationLoaderState {
    objectTitle: string;
    policyholderId: number;
}

export interface ConversationsPageState {
    title?: string;
    objectId?: string;
    objectType?: string;
    policyholderId?: number;
}
